/** Lead Pipeline types (Figma 4.1A). The board groups `/leads/` by `stage`. */

export type LeadStage =
  | "new_lead"
  | "contacted"
  | "tour_scheduled"
  | "qualified"
  | "won"
  | "lost";

/** A single lead card on the board. */
export interface Lead {
  id: string;
  /** Company name, or the primary contact's full name for individuals. */
  name: string;
  /** "Corporate" / "Individual" (humanised customer_type). */
  customerType: string;
  /** Event-type display name resolved from `/event-types/` ("" if none). */
  eventType: string;
  /** Formatted estimated value, e.g. "£4,200". */
  value: string;
  guests: number | null;
  /** Humanised source, e.g. "Website". */
  source: string;
  /** Event date label for the value row (preferred date, else created). */
  date: string;
  /** Relative created label shown bottom-right, e.g. "Today" / "Mar 15". */
  createdLabel: string;
  /** Created date as `YYYY-MM-DD` — used by the period range filter. */
  createdAt: string;
  highValue: boolean;
  stage: LeadStage;
  /** Short reference, e.g. "#0008" (derived from id — no API reference yet). */
  ref: string;
}

/** One Kanban column (may combine stages, e.g. Won / Lost). */
export interface LeadColumn {
  key: string;
  label: string;
  stages: LeadStage[];
  leads: Lead[];
}

/**
 * Board filter state (Figma 4.1A Filter panel). Applied client-side to the
 * loaded leads. Empty arrays = "no filter on this facet" (show all).
 */
export interface LeadFilters {
  sort: "newest" | "oldest";
  customerTypes: string[];
  eventTypes: string[];
  sources: string[];
  highValueOnly: boolean;
}

export const DEFAULT_LEAD_FILTERS: LeadFilters = {
  sort: "newest",
  customerTypes: [],
  eventTypes: [],
  sources: [],
  highValueOnly: false,
};

/**
 * Period range filter (Figma 4.1A "This Week" picker). `slug` is a shared
 * `DATE_RANGE_PRESETS` value; for `custom`, explicit ISO bounds are stored.
 */
export interface LeadRange {
  slug: string;
  label: string;
  customStart: string | null;
  customEnd: string | null;
}

export const DEFAULT_LEAD_RANGE: LeadRange = {
  slug: "this_week",
  label: "This Week",
  customStart: null,
  customEnd: null,
};

/** The 5 KPI stat cards (derived from the leads until a stats endpoint exists). */
export interface LeadStats {
  total: number;
  newToday: number;
  conversionPct: number | null;
  won: number;
  lost: number;
}

/** Stage → badge label + colour for the Won/Lost column cards. */
export const STAGE_BADGE: Partial<
  Record<LeadStage, { label: string; color: "success" | "error" }>
> = {
  won: { label: "Won", color: "success" },
  lost: { label: "Lost", color: "error" },
};
